/**
 * Lightweight i18n for the fan SPA — same shape as the admin-ui copy
 * (nested keys, JSON dicts, localStorage persistence) but independent
 * so each app can drift its strings without coupling builds.
 *
 * Active locale lives in localStorage under `nx.fan.locale`. Default
 * is zh-TW; falls back through en if a key is missing.
 */
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import zhTW from './zh-TW.json';
import en from './en.json';

export type Locale = 'zh-TW' | 'en';

const STORAGE_KEY = 'nx.fan.locale';
const DEFAULT_LOCALE: Locale = 'zh-TW';

type Dict = typeof zhTW;

const LOCALES: Record<Locale, Dict> = {
  'zh-TW': zhTW,
  en,
};

export const SUPPORTED_LOCALES: { code: Locale; labelKey: string }[] = [
  { code: 'zh-TW', labelKey: 'language.zh-TW' },
  { code: 'en', labelKey: 'language.en' },
];

interface I18nContextValue {
  locale: Locale;
  setLocale: (l: Locale) => void;
  t: (key: string, vars?: Record<string, string | number>) => string;
}

const I18nContext = createContext<I18nContextValue | null>(null);

function readStoredLocale(): Locale {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === 'zh-TW' || raw === 'en') return raw;
  } catch { /* localStorage might be unavailable */ }
  const nav = typeof navigator !== 'undefined' ? navigator.language : '';
  if (nav.startsWith('zh')) return 'zh-TW';
  if (nav.startsWith('en')) return 'en';
  return DEFAULT_LOCALE;
}

function walkDict(dict: unknown, path: string[]): string | null {
  let cur: unknown = dict;
  for (const seg of path) {
    if (cur && typeof cur === 'object' && seg in (cur as Record<string, unknown>)) {
      cur = (cur as Record<string, unknown>)[seg];
    } else {
      return null;
    }
  }
  return typeof cur === 'string' ? cur : null;
}

function interpolate(s: string, vars?: Record<string, string | number>): string {
  if (!vars) return s;
  return s.replace(/\{(\w+)\}/g, (_, k) => (k in vars ? String(vars[k]) : `{${k}}`));
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(() => readStoredLocale());

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, locale);
    } catch { /* same defensive guard */ }
    if (typeof document !== 'undefined') {
      document.documentElement.lang = locale;
    }
  }, [locale]);

  const value = useMemo<I18nContextValue>(() => {
    const dict = LOCALES[locale];
    const fallback = LOCALES[DEFAULT_LOCALE];
    return {
      locale,
      setLocale: setLocaleState,
      t: (key, vars) => {
        const path = key.split('.');
        const primary = walkDict(dict, path);
        if (primary != null) return interpolate(primary, vars);
        const fb = walkDict(fallback, path);
        if (fb != null) return interpolate(fb, vars);
        return key;
      },
    };
  }, [locale]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used inside <I18nProvider>');
  return ctx;
}
